'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { useQuery } from '@tanstack/react-query';
import { APP_WALLET, AppNavigation as AppNavigationComponent } from '@workspace/ui';
import { ThemeToggle } from '@workspace/ui/components/custom/menu/theme-toggle';
import { HELP_DESK_URL } from '@workspace/ui/components/layout/app-top-header-help-desk';
import { usePostHogBoolFlag } from '@workspace/analytics/posthog/feature-flags-client';
import {
  mapNotificationCounts,
  EMPLOYEE_STATUS,
  PERMISSIONS,
  TEAM_ROLES,
  CONTRACTOR_ROLE_VALUES,
  USER_ROLES,
  getShowPersonalTeamNavSubmenu,
  isExpensesBlockedByRoleContext,
} from '@workspace/ui/lib';
import type { MenuItem } from '@workspace/ui/types';
import { ClipboardList, Settings, SquarePlus, User, CircleHelp } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { getEmployeeByUserAndOrganisation } from '@/actions/employees';
import { getNotificationCounts } from '@/actions/notification';
import { Logout } from '@/components/custom/menu/logout';
import NotificationsMobile from '@/components/custom/menu/notifications-mobile';
import { MobileOrganisationSwitcher } from '@/components/layout/mobile-organisation-switcher';
import {
  useIsContractor,
  useIsGigWorker,
  useIsIndividualCraft,
  useIsB2CFree,
  useIsB2CStandard,
  useIsB2CEmployee,
} from '@/hooks/use-contractor';
import { useIsEmployer, useIsTeamsGlobalAdmin } from '@/hooks/use-employer';
import { useCanShowMultiOrganisationSwitcher } from '@/hooks/use-can-show-multi-organisation-switcher';
import { usePermissions } from '@/hooks/use-permission';
import { useRoles } from '@/hooks/use-roles';
import { useTeamRoles } from '@/hooks/use-team-roles';
import { useWalletNavSubitems } from '@/hooks/use-wallet-nav-subitems';
import { getActiveOrganisationFromUser } from '@/lib/active-organisation';
import { getEorHref, getEorSupportRequestsHref } from '@/lib/eor-app-url';
import { useAuthStore } from '@/stores/authStore';

function getInitials(firstName?: string, lastName?: string): string {
  const initials =
    `${firstName?.[0]?.toUpperCase() ?? ''}${lastName?.[0]?.toUpperCase() ?? ''}`.trim();

  return initials || 'NT';
}

export default function AppNavigation() {
  const pathname = usePathname();
  const isEmployerUser = useIsEmployer();
  const isContractorUser = useIsContractor();
  const isGigWorkerUser = useIsGigWorker();
  const isIndividualCraftUser = useIsIndividualCraft();
  const isB2CStandardUser = useIsB2CStandard();
  const isB2CFreeUser = useIsB2CFree();
  const isB2CEmployeeUser = useIsB2CEmployee();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { hasRole: hasTeamRole } = useTeamRoles();
  const isTeamLead = hasTeamRole(TEAM_ROLES.TEAM_LEAD);

  const isTeamMember = hasTeamRole(TEAM_ROLES.TEAM_MEMBER);
  const isGlobalAdmin = useIsTeamsGlobalAdmin();
  const user = useAuthStore((state) => state.user);
  const { hasAnyRole, hasRole } = useRoles();
  const hasContractorRole = hasAnyRole(CONTRACTOR_ROLE_VALUES);
  const hasEmployerRole = hasRole(USER_ROLES.EMPLOYER);
  const isContractorGlobalAdmin = isGlobalAdmin && (isContractorUser || hasContractorRole);
  const isContractorTeamLeadViewer =
    (isContractorUser || hasContractorRole) && isTeamLead && !isGlobalAdmin;
  const isContractorViewer = isContractorGlobalAdmin || isContractorTeamLeadViewer;
  const isContractor = isContractorUser || hasContractorRole || false;
  const hasTeamLeadOrAdminOnTeam =
    hasTeamRole(TEAM_ROLES.TEAM_LEAD) || hasTeamRole(USER_ROLES.TEAMS_GLOBAL_ADMIN);
  const showPersonalTeamNavSubmenu = useMemo(
    () =>
      getShowPersonalTeamNavSubmenu({
        isEmployer: isEmployerUser,
        isContractorViewer,
        isTeamsGlobalAdmin: isGlobalAdmin,
        hasTeamLeadOrAdminOnTeam: hasTeamLeadOrAdminOnTeam,
      }),
    [hasTeamLeadOrAdminOnTeam, isContractorViewer, isEmployerUser, isGlobalAdmin]
  );
  const canViewMyProfile = isEmployerUser && !isGlobalAdmin;
  const canViewProfile =
    !isEmployerUser &&
    (isContractor ||
      isGigWorkerUser ||
      isIndividualCraftUser ||
      isB2CStandardUser ||
      isB2CFreeUser);

  const showContractorPayments = usePostHogBoolFlag('show-contractor-payments', false);
  const showPayrollToRun = usePostHogBoolFlag('show-payroll-to-run', false);
  const showPayrollCalculator = usePostHogBoolFlag('show-payroll-calculator', false);
  const featureFlags = useMemo<Record<string, boolean>>(
    () => ({
      'show-contractor-payments': showContractorPayments,
      'show-payroll-to-run': showPayrollToRun,
      'show-payroll-calculator': showPayrollCalculator,
    }),
    [showContractorPayments, showPayrollToRun, showPayrollCalculator]
  );

  const userId = user?.user?.id;
  const organisationId = getActiveOrganisationFromUser(user)?.id;
  const showExpenses = !isExpensesBlockedByRoleContext(getActiveOrganisationFromUser(user));
  const canShowOrganisationSwitcher = useCanShowMultiOrganisationSwitcher();
  const initials = getInitials(user?.user.first_name, user?.user.last_name);
  const mobileProfileItems = useMemo<MenuItem[]>(
    () => [
      {
        title: 'Settings',
        url: getEorHref('/settings'),
        icon: <Settings />,
      },
      ...(canViewMyProfile
        ? [
            {
              title: 'My Profile',
              url: getEorHref('/my-profile'),
              icon: <User />,
            },
          ]
        : []),
      ...(canViewProfile
        ? [
            {
              title: 'My Profile',
              url: getEorHref('/profile'),
              icon: <User />,
            },
          ]
        : []),
      {
        title: 'Theme',
        component: <ThemeToggle />,
      },
      {
        title: 'Request support',
        url: getEorSupportRequestsHref({ open: true, from: pathname }),
        icon: <SquarePlus />,
      },
      {
        title: 'My support requests',
        url: getEorSupportRequestsHref(),
        icon: <ClipboardList />,
      },
      {
        title: 'Help desk',
        url: HELP_DESK_URL,
        icon: <CircleHelp />,
        openInNewTab: true,
      },
      {
        title: 'Logout',
        component: <Logout />,
        bottomSeparated: true,
      },
    ],
    [canViewMyProfile, canViewProfile, pathname]
  );

  const { data: employeeResponse } = useQuery({
    queryKey: ['employee-by-user-and-organisation', userId, organisationId],
    queryFn: () => getEmployeeByUserAndOrganisation(userId!, organisationId!),
    enabled:
      !!userId &&
      !!organisationId &&
      !isEmployerUser &&
      !isContractorUser &&
      !isGigWorkerUser &&
      !isIndividualCraftUser &&
      !isB2CStandardUser &&
      !isB2CFreeUser &&
      !isB2CEmployeeUser &&
      !isContractorGlobalAdmin,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: countsData } = useQuery({
    queryKey: ['notifications', 'counts'],
    queryFn: getNotificationCounts,
    refetchOnWindowFocus: true,
  });

  const notificationCounts = useMemo(
    () => mapNotificationCounts(countsData?.data ?? []),
    [countsData?.data]
  );

  const isEmployed = employeeResponse?.data?.status === EMPLOYEE_STATUS.EMPLOYED;
  const canViewSelfPayslips =
    hasPermission(PERMISSIONS.VIEW_SELF_PAYSLIP_DOCUMENTS) && isContractor;
  const canViewPayrollToRun = hasAnyPermission([
    PERMISSIONS.VIEW_OWN_PAYROLL_FINAL,
    PERMISSIONS.VIEW_OWN_PAYROLLS,
    PERMISSIONS.VIEW_IN_OWN_PAYROLLS,
  ]);
  const walletSubitems = useWalletNavSubitems();

  return (
    <AppNavigationComponent
      app={APP_WALLET}
      eorBase={process.env.NEXT_PUBLIC_FE_URL_EOR || ''}
      isEmployer={isEmployerUser}
      // Some users keep `accessible_app: employee` in the JWT while also holding contractor
      // roles. Navigation treats them as contractor global admins, not as employees.
      isContractor={isContractor}
      isGigWorker={isGigWorkerUser}
      isIndividualCraft={isIndividualCraftUser}
      isB2CStandard={isB2CStandardUser}
      isB2CFree={isB2CFreeUser}
      isB2CEmployee={isB2CEmployeeUser}
      isEmployed={isEmployed}
      isTeamLead={isTeamLead}
      isTeamMember={isTeamMember}
      isGlobalAdmin={isGlobalAdmin}
      showAbsenceSubmenu={showPersonalTeamNavSubmenu}
      showExpenses={showExpenses}
      showExpenseSubmenu={showPersonalTeamNavSubmenu}
      showMonthlyInputsSubmenu={showPersonalTeamNavSubmenu}
      hasEmployerRole={hasEmployerRole}
      canViewSelfPayslips={canViewSelfPayslips}
      canViewPayrollToRun={canViewPayrollToRun}
      showNotifications={false}
      showFooterMenuItems={false}
      showHelpDeskInSidebar={false}
      slotNotifications={null}
      renderNotificationsMobile={({ isOpen, setIsOpen }) => (
        <NotificationsMobile isOpen={isOpen} setIsOpen={setIsOpen} />
      )}
      renderOrganisationSwitcherMobile={
        canShowOrganisationSwitcher
          ? ({ isOpen, setIsOpen }) => (
              <MobileOrganisationSwitcher isOpen={isOpen} setIsOpen={setIsOpen} />
            )
          : undefined
      }
      slotLogout={null}
      mobileProfileInitials={initials}
      mobileProfileItems={mobileProfileItems}
      featureFlags={featureFlags}
      notificationCounts={notificationCounts}
      walletSubitems={walletSubitems}
    />
  );
}
